import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Download, Inbox, FileText, Users } from "lucide-react";
import api, { API } from "@/lib/api";

const TABS = [
  { key: "quotes", label: "Quote Requests" },
  { key: "contact", label: "Contact Enquiries" },
  { key: "applications", label: "Job Applications" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Field = ({ label, value }) =>
  value ? (
    <p className="text-sm"><span className="text-slate-400 text-xs uppercase tracking-wider block">{label}</span><span className="text-slate-800 break-words">{value}</span></p>
  ) : null;

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("quotes");
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [enq, apps, st] = await Promise.all([
        api.get("/admin/enquiries"),
        api.get("/admin/applications"),
        api.get("/admin/stats"),
      ]);
      setEnquiries(enq.data);
      setApplications(apps.data);
      setStats(st.data);
    } catch {
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-navy" />
      </div>
    );
  }

  const quotes = enquiries.filter((e) => e.type === "quote");
  const contacts = enquiries.filter((e) => e.type === "contact");

  const renderEnquiry = (e) => (
    <div key={e.id} className="bg-white border border-slate-200 p-6" data-testid={`enquiry-item-${e.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="font-heading font-bold text-navy">{e.name}</p>
        <span className="text-xs text-slate-400">{fmtDate(e.created_at)}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Email" value={e.email} />
        <Field label="Phone" value={e.phone} />
        <Field label="Organization" value={e.organization} />
        <Field label="Service" value={e.service} />
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <Field label="Message" value={e.message} />
      </div>
    </div>
  );

  const renderApplication = (a) => (
    <div key={a.id} className="bg-white border border-slate-200 p-6" data-testid={`application-item-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="font-heading font-bold text-navy">{a.name} <span className="ml-2 text-xs font-body font-semibold text-gold-hover uppercase tracking-wider">{a.position}</span></p>
        <span className="text-xs text-slate-400">{fmtDate(a.created_at)}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Email" value={a.email} />
        <Field label="Phone" value={a.phone} />
        <Field label="Experience" value={a.experience} />
      </div>
      {a.message && <div className="mt-3 pt-3 border-t border-slate-100"><Field label="Message" value={a.message} /></div>}
      <a
        href={`${API}/admin/applications/${a.id}/resume`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`resume-download-${a.id}`}
        className="mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-navy border border-slate-300 hover:border-gold hover:text-gold-hover px-4 py-2.5 transition-colors"
      >
        <Download size={13} /> {a.resume_filename || "Download Resume"}
      </a>
    </div>
  );

  const listForTab = tab === "quotes" ? quotes : tab === "contact" ? contacts : applications;
  const renderItem = tab === "applications" ? renderApplication : renderEnquiry;

  return (
    <div className="min-h-screen bg-[#FAFAFC]" data-testid="admin-dashboard">
      <header className="bg-navy text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gold flex items-center justify-center">
              <span className="font-heading font-extrabold text-navy leading-none">D</span>
            </div>
            <span className="font-heading font-bold text-sm tracking-wide">Daksh Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-slate-300 hover:text-gold hidden sm:block" data-testid="admin-view-site">View Website</Link>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase border border-white/30 hover:border-gold hover:text-gold px-4 py-2 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy tracking-tight">Welcome{user?.name ? `, ${user.name}` : ""}</h1>
        <p className="text-sm text-slate-500 mt-1">Enquiries, quote requests and job applications from the website.</p>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8" data-testid="admin-stats">
            {[
              { icon: FileText, label: "Quote Requests", value: stats.quotes },
              { icon: Inbox, label: "Total Enquiries", value: stats.enquiries },
              { icon: Users, label: "Job Applications", value: stats.applications },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 p-6 flex items-center gap-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 bg-navy flex items-center justify-center">
                  <s.icon size={19} className="text-gold" />
                </div>
                <div>
                  <p className="font-heading text-2xl font-extrabold text-navy leading-none">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1 mt-10 border-b border-slate-200" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-xs font-bold tracking-widest uppercase transition-colors border-b-2 -mb-px ${
                tab === t.key ? "border-gold text-navy" : "border-transparent text-slate-400 hover:text-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-5">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-navy" /></div>
          ) : listForTab.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 p-16 text-center text-sm text-slate-400" data-testid="admin-empty-state">
              No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} yet.
            </div>
          ) : (
            listForTab.map(renderItem)
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
